/**
 * The "Daily follow-up email" card on realtor settings (R26, R27).
 *
 * With an address it shows where the email goes and a switch to turn it off.
 * Without one it says the email needs an address, and offers to connect a
 * Google account when both the server and this build can do that. Linking
 * Google gives the email the Google account's address.
 */

import { type CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { isGoogleEnabled } from "@/features/auth/components/google-signin-button";
import { linkGoogle } from "@/features/auth/services/auth.service";
import {
	type DigestSettings,
	getDigestSettings,
	updateDigestSettings,
} from "@/features/realtor/services/digest-settings-api.service";
import { queryKeys } from "@/lib/query-keys";

/** The backend's message for a failed request, when it sent one. */
function backendMessage(error: unknown): string | undefined {
	return (error as { response?: { data?: { message?: string } } }).response
		?.data?.message;
}

export function DigestSettingsCard() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const settingsQuery = useQuery({
		queryKey: queryKeys.realtor.digestSettings(),
		queryFn: getDigestSettings,
	});

	const toggle = useMutation({
		mutationFn: (enabled: boolean) => updateDigestSettings(enabled),
		onSuccess: (settings) => {
			queryClient.setQueryData(queryKeys.realtor.digestSettings(), settings);
			toast.success(
				settings.enabled
					? t("realtor.settings.digest.turnedOn")
					: t("realtor.settings.digest.turnedOff"),
			);
		},
		onError: (error) => {
			toast.error(
				backendMessage(error) ?? t("realtor.settings.digest.saveFailed"),
			);
		},
	});

	const link = useMutation({
		mutationFn: (idToken: string) => linkGoogle(idToken),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.digestSettings(),
			});
			toast.success(t("realtor.settings.digest.googleLinked"));
		},
		onError: (error) => {
			toast.error(
				backendMessage(error) ?? t("realtor.settings.digest.googleLinkFailed"),
			);
		},
	});

	const onGoogleCredential = (response: CredentialResponse) => {
		if (!response.credential) {
			toast.error(t("realtor.settings.digest.googleLinkFailed"));
			return;
		}
		link.mutate(response.credential);
	};

	return (
		<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
			<CardContent className="space-y-3 p-4">
				<div>
					<h2
						className="text-sm font-medium"
						style={{ color: "var(--color-dashboard-text)" }}
					>
						{t("realtor.settings.digest.title")}
					</h2>
					<p
						id="digest-description"
						className="text-xs"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("realtor.settings.digest.description")}
					</p>
				</div>

				{settingsQuery.isPending && <Skeleton className="h-10 w-full" />}

				{settingsQuery.isError && (
					<QueryErrorState
						message={t("realtor.settings.digest.loadFailed")}
						onRetry={() => void settingsQuery.refetch()}
						isRetrying={settingsQuery.isFetching}
					/>
				)}

				{settingsQuery.data && (
					<DigestBody
						settings={settingsQuery.data}
						saving={toggle.isPending}
						linking={link.isPending}
						onToggle={(enabled) => toggle.mutate(enabled)}
						onGoogleCredential={onGoogleCredential}
						onGoogleError={() =>
							toast.error(t("realtor.settings.digest.googleLinkFailed"))
						}
					/>
				)}
			</CardContent>
		</Card>
	);
}

function DigestBody({
	settings,
	saving,
	linking,
	onToggle,
	onGoogleCredential,
	onGoogleError,
}: {
	settings: DigestSettings;
	saving: boolean;
	linking: boolean;
	onToggle: (enabled: boolean) => void;
	onGoogleCredential: (response: CredentialResponse) => void;
	onGoogleError: () => void;
}) {
	const { t } = useTranslation();

	if (settings.address) {
		return (
			<div className="flex items-start justify-between gap-4">
				<div className="min-w-0 space-y-1">
					<label
						htmlFor="digest-toggle"
						className="text-sm font-medium"
						style={{ color: "var(--color-dashboard-text)" }}
					>
						{t("realtor.settings.digest.toggleLabel")}
					</label>
					<p
						className="break-all text-xs"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{settings.addressSource === "google"
							? t("realtor.settings.digest.sendsToGoogle", {
									address: settings.address,
								})
							: t("realtor.settings.digest.sendsTo", {
									address: settings.address,
								})}
					</p>
				</div>
				<Switch
					id="digest-toggle"
					aria-describedby="digest-description"
					checked={settings.enabled}
					disabled={saving}
					onCheckedChange={onToggle}
				/>
			</div>
		);
	}

	const canLinkGoogle = settings.googleLinkAvailable && isGoogleEnabled();
	return (
		<div className="space-y-3">
			<p className="text-sm" style={{ color: "var(--color-dashboard-text)" }}>
				{t("realtor.settings.digest.noAddress")}{" "}
				{canLinkGoogle
					? t("realtor.settings.digest.linkGoogleHint")
					: t("realtor.settings.digest.queueOnly")}
			</p>
			{canLinkGoogle && (
				<div aria-busy={linking || undefined}>
					<GoogleLogin
						onSuccess={onGoogleCredential}
						onError={onGoogleError}
						text="continue_with"
						width="280"
					/>
				</div>
			)}
		</div>
	);
}
